import { useRef, useState, useEffect, Suspense, lazy  } from "react";

import axios from 'axios';

import { date } from '@/utils/Dates';
import { html } from '@/utils/Format';
import { getErrorMessage } from '@/utils/Errors';

import Button from '@/components/Button';
import { Collapse, Chip } from '@mui/material';

import { 
    ArrowBack as BackIcon, 
    Lock as CloseIcon,
    LockOpen as OpenIcon,
    Create as AnswerIcon,
    Send as SendIcon,
    Close as CancelIcon,
    PersonAddAlt as AssignIcon
} from '@mui/icons-material';

const TicketModal = lazy(() => import('@/modals/TicketModal'));
import InputRichText from '@/components/InputRichText';
import Dropdown from '@/components/Dropdown';
import Switch from '@/components/Switch';

// ticket is params
export default function Ticket({
    t,language,table,user,apiUrl,token,ticket,columns,response,
    handleBack,router,handleLogin,sendMessage,fullScreen,viewPath}
){

    const containerRef = useRef(null);
    const answersRef = useRef(null);

    // owner means the customxer
    // if I am worker:
    // if I open the ticket but assigned to another person, I am the customer
    // if I open the ticket but assigned to myself, I am not customer
    const [ isOwner ] = useState(
        (user?.id && ticket?.user_id) ?
            parseInt(user?.id) !== parseInt(ticket?.user_id) ?
                false
            :
                !response?.data?.manage ?
                    true
                :
                    (ticket?.assigned && parseInt(user?.id) !== parseInt(ticket?.assigned)) ?
                        true
                    :
                        false
        :
            false
    );

    const [ creator, setCreator ] = useState('');
    const [ company, setCompany ] = useState('');
    const [ assigned, setAssigned ] = useState('');

    const [ openName, setOpenName ] = useState('');
    const [ openColor, setOpenColor ] = useState('');

    const [ statusName, setStatusName ] = useState('');
    const [ statusColor, setStatusColor ] = useState('');

    // if opening default status is:
    // if worker => waiting for customer (2)
    // if customer => working (1)
    // if closing, default status is solved (3)
    const [ defaultTicketStatus ] = useState([
        columns?.ticket_status_id?.values.filter((s)=> s?.id === 3)?.pop(),
        columns?.ticket_status_id?.values.filter((s)=> s?.id === (isOwner ? 1 : 2))?.pop(),
    ]);

    const [ ticketReplies, setTicketReplies ] = useState(ticket?.replies);

    // to set the ticket creator
    const setTicketCreator = () => {

        // if we are owner of ticket, get our user info
        if(isOwner){
            setCreator(user?.name+' ('+user?.email+')');
        }
        else{
            // if admin, we have user_id on columns
            if(columns?.user_id){
                const creators = columns?.user_id?.values.filter((u)=> u?.id === ticket?.user_id);                
                setCreator(creators && creators?.length > 0 ? creators?.pop()?.name : '');

                const companies = columns?.company_id?.values.filter((u)=> u?.id === ticket?.company_id);
                setCompany(companies && companies?.length > 0 ? companies?.pop()?.name : '');

                const assignedUsers = columns?.assigned?.values.filter((u)=> u?.id === ticket?.assigned);
                setAssigned(assignedUsers && assignedUsers?.length > 0 ? assignedUsers?.pop()?.name : '');
            }
        }
    }

    // to set the ticket open
    const setTicketOpen = () => {
        const name = ticket?.open ? t('tickets.details.open') : t('tickets.details.closed');
        setOpenName(name);
        setOpenColor(ticket?.open ? 'error' : 'success');
        setReplyTicketOpen(ticket?.open);
    }

    // to set the ticket status
    const setTicketStatus = () => {
        
        const statusesNames = columns?.ticket_status_id?.values.filter((s)=> s?.id === ticket?.ticket_status_id);
        const element = statusesNames && statusesNames?.length > 0 ? statusesNames.pop() : null;
        
        const name = element ? t('tickets.ticket_status_id.'+element?.name) : '';
        setStatusName(name);
        setStatusColor(columns?.ticket_status_id?.colors[ticket?.ticket_status_id] ? columns?.ticket_status_id?.colors[ticket?.ticket_status_id] : '');
    }

    // to set ticket configuration
    const setTicketConfig = () => {
        setTicketCreator();
        setTicketOpen();
        setTicketStatus();
    }

    // to change status and open/close when answering ticket    
    const [ replyTicketOpen, setReplyTicketOpen ] = useState(ticket?.open ? 1 : 0);
    const [ replyTicketStatus, setReplyTicketStatus ] = useState(defaultTicketStatus[ticket?.open ? 1 : 0]);

    const onChangeOpen = (e) => {  
        
        var value = e.target.checked;

        // inverted values
        // true means closed
        var open = value ? false : true;
        
        setReplyTicketOpen(open);

        // set default status
        setReplyTicketStatus(defaultTicketStatus[open ? 1 : 0]);
    }

    const onChangeStatus = (newItem) => {
        setReplyTicketStatus(newItem);
    }

    useEffect(() => {                
        
        setTicketConfig();

        // scroll to top
        if(ticket && answersRef.current){
            answersRef.current.scrollIntoView(/*{ 
                behavior: 'smooth',
                block: 'start'
            }*/);
        }

    },[ticket]);

    // to go back to the tickets' list
    const handleButtonBack = () => {        
        
        // to avoid that it goes again to ticket details
        var url = '/'+table;
        router.navigate(url);
        //window.history.pushState({path:url},'',url);

        handleBack();
    }

    // to assign the ticket
    const handleButtonAssign = () => {

        var apiPath = '/tickets/assign';

        const config = {
            headers: { Authorization: `Bearer ${token}` } 
        };

        const userId = parseInt(user?.id);

        const send = {
            language: language,
            companyuser: user?.company,
            ids: ticket?.id,
            user: userId
        };

        var errorMessage = t('tickets.update.error');            

        axios.put(apiUrl+apiPath,send,config)
        .then(function (response) {
            
            if(response?.data){
          
                var responseData = response.data;

                // if result is ok (1)                
                if(responseData?.result){              
                    
                    sendMessage(t('tickets.assigned.successone'));

                    // update ticket

                    ticket.assigned = userId;

                    const assignedUsers = columns?.assigned?.values.filter((u)=> u?.id === userId);
                    setAssigned(assignedUsers && assignedUsers?.length > 0 ? assignedUsers?.pop()?.name : '');                    
                } 
                else{

                    if(responseData?.status && responseData.status === 401){
                        handleLogin();
                    }
                    else{                       
                        // check error message
                        if(responseData?.data?.reasons){                            
                            const reasons = responseData?.data?.reasons;
                            errorMessage = getErrorMessage(t,'tickets',reasons);
                        }
                        
                        sendMessage(errorMessage,'error');
                    }
                }
            }
            else{
                sendMessage(errorMessage,'error');   
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();
            }
            else{                    
                sendMessage(errorMessage,'error');
            }
        });
    }

    // modal to confirm closure
    const [ showConfirm, setShowConfirm ] = useState(false);

    // if confirming open/close
    const handleConfirm = (e,answer,status) => {

        // close modal
        setShowConfirm(false);

        // if yes, open/close ticket
        if(answer){            

            var apiPath = '/'+table+'/edit';

            const config = {
                headers: { Authorization: `Bearer ${token}` } 
            };

            // the opposite. if open => closed, if closed => open
            const opening = ticket?.open ? 0 : 1;

            // adding unnecessary data but to avoid getting error because missing information

            // get ticket status
            // customer
            // if opening => open with working (1)
            // if closing => choose status to cancel
            // worker
            // choose status to open and close
            let ticketNewStatus;
            if(isOwner){
                if(opening){
                    ticketNewStatus = 1;                    
                }
                else{
                    ticketNewStatus = status;
                }
            }
            else{
                ticketNewStatus = status;
            }

            const send = {
                language: language,
                companyuser: user?.company,
                id: ticket?.id, // to avoid adding instead of updating
                title: ticket?.title, // to avoid message error for empty title
                open: opening,
                ticket_status_id: ticketNewStatus
            };

            var errorMessage = t('tickets.update.error');            

            axios.put(apiUrl+apiPath,send,config)
            .then(function (response) {
                
                if(response?.data){
              
                    var responseData = response.data;

                    // if result is ok (1)                
                    if(responseData?.result){              
                        
                        sendMessage(t(`tickets.update.${ticket?.open ? 'closed' : 'open'}`));

                        // udpate open/closed
                        ticket.open = ticket?.open ? false : true; // the opposite. if open => closed, if closed => open
                        ticket.ticket_status_id = ticketNewStatus;

                        // update date
                        ticket.updated_at = responseData?.data?.item?.updated_at;
                        
                        // update ticket status and open/closed
                        setTicketOpen();
                        setTicketStatus();

                        // close reply input if open
                        setShowNewAnswer(false);
                    } 
                    else{

                        if(responseData?.status && responseData.status === 401){
                            handleLogin();
                        }
                        else{                       
                            // check error message
                            if(responseData?.data?.reasons){                            
                                const reasons = responseData?.data?.reasons;
                                errorMessage = getErrorMessage(t,table,reasons);
                            }
                            
                            sendMessage(errorMessage,'error');
                        }
                    }
                }
                else{
                    sendMessage(errorMessage,'error');   
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();
                }
                else{                    
                    sendMessage(errorMessage,'error');
                }
            });
        }
        else{
            // close confirmation modal if clicking No
            setShowConfirm(false);
        }
    }

    const [ showNewAnswer, setShowNewAnswer ] = useState(false);
    const [ newAnswer, setNewAnswer ] = useState('');
    const [ newAnswerError ] = useState('');

    const handleNewAnswer = (value) => {
        setNewAnswer(value);
    }

    const handleSendNewAnswer = () => {

        // message cannot be empty
        if(!newAnswer || newAnswer.length === 0){
            var errorText = t('tickets.errors.reply');
            sendMessage(errorText,'error');
        }
        else{

            const apiPath = '/ticket_replies';

            const config = {
                headers: { Authorization: `Bearer ${token}` } 
            };        

            const send = {
                language: language,
                companyuser: user?.company,
                ticket_id: ticket?.id,
                ticket_user_id: ticket?.user_id,
                reply: newAnswer
            };

            // check if worker because the open and status can be changed
            if(!isOwner){
                send.ticket_open = replyTicketOpen ? 1 : 0;
                send.ticket_status_id = replyTicketStatus?.id;
            }

            var errorMessage = t('actions.add.error');
            const url = apiUrl+apiPath+'/add';
            
            axios.post(url,send,config)
            .then(function (response) {
                    
                if(response?.data){
              
                    var responseData = response.data;

                    // if result is ok (1)                
                    if(responseData?.result){              
                        
                        sendMessage(t('actions.send.success'),'success');
                        const newItemData = responseData.data.item;

                        // new answer goes above the others because it is ordered by created_at desc
                        const newTicketReplies = [newItemData,...ticketReplies];
                        setTicketReplies(newTicketReplies);

                        // hide editor
                        setNewAnswer('');
                        setShowNewAnswer(false);

                        // if customer and sending answer, change status to working (1)
                        // if not customer and sending answer, status and open are set on the reply
                        if(isOwner){
                            ticket.ticket_status_id = 1;
                        }
                        else{
                            ticket.open = replyTicketOpen;
                            setTicketOpen();

                            ticket.ticket_status_id = replyTicketStatus?.id;                            
                        }

                        // update ticket status                        
                        setTicketStatus();

                        // update date on the ticket
                        // it must be the same as the reply update date
                        ticket.updated_at = newItemData?.updated_at;

                        // update replies
                        ticket.replies = newTicketReplies;
                    } 
                    else{

                        if(responseData?.status && responseData.status === 401){
                            handleLogin();
                        }
                        else{                       
                            // check error message
                            if(responseData?.data?.reasons){                            
                                const reasons = responseData?.data?.reasons;
                                errorMessage = getErrorMessage(t,table,reasons);
                            }

                            sendMessage(errorMessage,'error');
                        }
                    }
                }
                else{
                    sendMessage(errorMessage,'error');   
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();
                }
                else{                   
                    sendMessage(errorMessage,'error');
                }
            });
        }
    }

    return (
        <>
        {
            showConfirm &&
            <Suspense>
                <TicketModal 
                    t={t}
                    open={showConfirm} 
                    setOpen={setShowConfirm} 
                    isOwner={isOwner}
                    opening={ticket?.open ? 0 : 1}
                    title={ticket?.open ? t('actions.close.title') : t('actions.open.title')}
                    text={ticket?.open ? t('actions.close.confirm') : t('actions.open.confirm')}
                    statuses={columns?.ticket_status_id?.valuesall.filter((s)=> s.parent === (ticket?.open ? 0 : 1))}
                    handleAnswer={handleConfirm}
                    fullScreen={fullScreen}
                />
            </Suspense>
        }
        <div className={`w-full ${viewPath === 'fullview' && 'pt-4'}`}>
            <div className="flex justify-center items-center mx-1">
                <div className="flex justify-center">
                    <div className='me-1'>
                        <BackIcon className='action-button-modal text-black' onClick={handleButtonBack}/>
                    </div>
                    {
                        !isOwner &&
                        <div className='me-1'>
                            <AssignIcon className='action-button-modal text-purple-600' onClick={handleButtonAssign}/>
                        </div>
                    }
                    <div className=''>
                        {
                            ticket?.open ?
                                <CloseIcon className='action-button-modal text-red-600' onClick={() => setShowConfirm(true)}/>
                            :
                                <OpenIcon className='action-button-modal text-green-600' onClick={() => setShowConfirm(true)}/>
                        }
                    </div>
                </div>
                {/*
                <div className="">
                    <Button
                        onClick={handleButtonBack}
                        //text={t('actions.back.button')}
                        type='back'
                        icon={<BackIcon/>}
                    />
                </div>  

                <div className="mx-1">
                        
                    <Button            
                        onClick={() => setShowConfirm(true)}
                        text={ticket?.open ? t('actions.close.button') : t('actions.open.button')}
                        type={ticket?.open ? "close" : "open"}
                        startIcon={ticket?.open ? <CloseIcon/> : <OpenIcon/>}
                    />
                </div>
                */}
            </div>
        </div>
        <div className="text-2xl font-bold text-center mb-2 border-b mt-8">
            {ticket?.title}
        </div>
        <div className='pb-2 mb-4'>
            {
                !isOwner &&
                <div className="text-sm text-center">
                    #{ticket?.id}                
                </div>
            }
            <div className="text-sm text-center">
                {date(ticket?.created_at,true,true)}                
            </div>   
            {
                creator && creator.length > 0 &&
                <div className="text-sm text-center">
                    {creator}
                </div>     
            }            
            {
                company && company.length > 0 &&
                <div className="text-sm text-center">
                    {company}
                </div>     
            }
            {
                !isOwner && assigned && assigned.length > 0 &&
                <div className="text-sm text-center font-bold">
                    {t('tickets.fields.assigned')+': '+assigned}
                </div>     
            }
            <div className="text-center font-bold mt-4 mb-2">
                <Chip                   
                    label={openName}
                    color={openColor}                
                />
            </div>
            <div className="text-center font-bold">
                <Chip                  
                    label={statusName}
                    color={statusColor}                                
                />
            </div>
        </div>
        {
            (ticket?.description && ticket.description?.length > 0) &&
            <div className='p-4  text-center'>
                <div className='font-bold'>
                    {t('tickets.fields.description')}:
                </div>
                <div dangerouslySetInnerHTML={{__html: html(ticket.description)}}></div>
            </div>
        }
        {
            ticket?.open &&
            <div ref={containerRef}>
                <Collapse 
                    in={showNewAnswer}
                    timeout={500}                    
                    container={containerRef.current}
                >               
                    <div>
                        <div className='mt-4 mb-2'>
                            <InputRichText
                                name='answer'
                                value={newAnswer}                      
                                placeholder={t('tickets.answer.new')}
                                onChange={handleNewAnswer}
                                error={newAnswerError && newAnswerError?.length > 0 ? newAnswerError : ''} 
                            />
                        </div>
                        {
                            !isOwner &&
                            <>
                            <div className="flex justify-center items-center mx-1 mb-2">
                                <div className=''>
                                    <Switch                                                                    
                                        name='replyTicketOpen'
                                        checked={!replyTicketOpen}
                                        //label={t('tickets.details.open')}
                                        onChange={onChangeOpen}  
                                        multilabel={true}
                                        labeldisabled={t('tickets.details.open')}
                                        labelenabled={t('tickets.details.closed')}                            
                                    />                       
                                </div>
                            </div>
                            <div className="flex justify-center items-center mx-1 mb-4">
                                <div className='mx-4'>
                                    <Dropdown        
                                        t={t}
                                        translate='tickets.ticket_status_id'
                                        type='search'
                                        name='ticket_status'
                                        value={replyTicketStatus}
                                        options={columns?.ticket_status_id?.valuesall.filter((s)=> s.parent === (replyTicketOpen ? 1 : 0))}
                                        handleDropdown={onChangeStatus}
                                        //error={}
                                    />                     
                                </div>
                            </div>
                            </> 
                        }
                        <div className="flex justify-center items-center mx-1">
                            <div className=''>
                                <Button
                                    onClick={handleSendNewAnswer}
                                    text={t('actions.send.button')}
                                    type='add'
                                    startIcon={<SendIcon/>}
                                />                        
                            </div>
                            <div className='mx-1'>
                                <Button
                                    onClick={() => setShowNewAnswer(false)}
                                    text={t('actions.close.button')}
                                    type='delete'
                                    startIcon={<CancelIcon/>}
                                />                        
                            </div>
                        </div> 
                    </div> 
                </Collapse> 
            </div>
        }
        {         
            ticket?.open && !showNewAnswer &&
            <div className="mt-4 text-center">                        
                <Button
                    onClick={() => setShowNewAnswer(true)}
                    text={t('tickets.answer.new')}
                    type='add'
                    startIcon={<AnswerIcon/>}
                />                        
            </div>            
        }
        <div ref={answersRef} className='pt-1'>
        {
            (ticketReplies && ticketReplies?.length > 0) &&
            <div className='mt-4 mb-16'>
                {
                    ticketReplies?.map((reply) => {

                        // check if customer's answer or not
                        var isOwnerAnswer = parseInt(ticket?.user_id) === parseInt(reply?.user_id);

                        // check if answer comes from opposite
                        var isOpposite = (isOwner && !isOwnerAnswer) || (!isOwner && isOwnerAnswer);

                        // if is owner, show generic name por company
                        var replier = null;
                        if(isOwner){
                            if(isOpposite){
                                //replier = t('tickets.company');
                                replier = import.meta.env.VITE_APP_NAME;
                            }
                            else{
                                replier = creator;
                            }
                        }
                        else{                        
                            var repliers = columns?.user_id?.values.filter((u)=> u?.id === reply?.user_id);
                            if(repliers && repliers?.length > 0){
                                replier = repliers.pop().name;
                            }
                        }

                        return (
                            <div 
                                key={'ticket'+ticket?.id+'_reply_'+reply?.id}
                                className={`border rounded p-2 mt-4 ${isOpposite && 'app-bg-gradient'}`}
                            >
                                <div className={`text-xs ${isOpposite ? 'text-right' : 'text-right'}`}>
                                    {date(reply?.created_at,true,true)}
                                </div>
                                {
                                    replier &&
                                    <div className={`text-xs mb-6 ${isOpposite ? 'text-right' : 'text-right'}`}>
                                        {replier}
                                    </div>
                                }                                
                                <div dangerouslySetInnerHTML={{__html: html(reply?.reply)}}></div>
                            </div>
                        );
                    })
                }
            </div>
        }
        </div>
        {/*
        <div className="w-full">            
            <div className="flex justify-center items-center mx-1 mt-4">
                <div className="">
                    <Button
                        onClick={handleButtonBack}
                        text={t('actions.back.button')}
                        type='back'
                        startIcon={<BackIcon/>}
                    />
                </div>  

                <div className="mx-1">
                        
                    <Button            
                        onClick={() => setShowConfirm(true)}
                        text={ticket?.open ? t('actions.close.button') : t('actions.open.button')}
                        type={ticket?.open ? "close" : "open"}
                        startIcon={ticket?.open ? <CloseIcon/> : <OpenIcon/>}
                    />
                </div>

            </div>
        </div>
        */}
        </>
    );
}